import type { GraphemeLessonPart } from "@-/dev.improve.haniverse.lib.level";
import type { NextDueDrillCardData } from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import { drillTypeCodec, type DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { Unit } from "@-/dev.improve.haniverse.lib.lesson.get";
import type { Level } from "@-/dev.improve.haniverse.lib.level";

export type MorphemeUnit = Extract<Unit, { type: "morpheme" }>;
export type SlideUnit = Extract<Unit, { type: "slide" }>;

/**
 * Where a quiz is being shown: in the `lesson` that teaches it (its first answer enrolls it), or
 * among the `review`s once it is due. The card and its write are the same; what follows differs.
 */
export type LearnCardQuizSource = "lesson" | "review";

/**
 * Where the Learn flow goes next, as DATA. Resolved once from committed state, then either
 * prepared as the underlying card of the deck or navigated to — the two consumers must agree, and
 * they do because they share this value rather than each re-deriving a route.
 *
 *   * `review` — the next due drill.
 *   * `lesson` — a new card unit (Learn deck name for presenting a curriculum card).
 *   * `slide` — an authored slide unit at its sequence position.
 *   * `quiz` — an authored quiz: a new one at its sequence position, or a due one.
 *   * `summary` — back to the Learn summary; `reason` says why the flow ended.
 *   * `exit` — leave the lesson stack (nothing left in the sequence and nothing due).
 */
export type LearnCardDestination =
  | { card: NextDueDrillCardData; kind: "review" }
  | { kind: "lesson"; lesson: MorphemeUnit }
  | { kind: "slide"; lesson: SlideUnit }
  | { kind: "quiz"; quizSlug: string; source: LearnCardQuizSource }
  | {
      kind: "summary";
      reason:
        | "catalogue-exhausted"
        | "new-cards-disabled"
        | "review-focus-complete"
        | "voice-complete";
    }
  | { kind: "exit" };

/**
 * The card the learner is LEAVING. Each kind has its own scheduling rules for what follows:
 * reviews run the due queue with the progress-bar lesson cadence, lessons walk the sequence
 * positionally and hand back to reviews at a lesson-group boundary, slides page through their run.
 * A quiz follows the rules of where it was shown: a lesson quiz those of the last slide of a run,
 * a review quiz those of a review.
 */
export type LearnCardDestinationOrigin =
  | { drillType?: DrillType; grapheme: string; kind: "review" }
  | { grapheme: string; kind: "lesson"; level: Level; part?: GraphemeLessonPart }
  | { kind: "slide"; slideSlug: string }
  | { kind: "quiz"; quizSlug: string; source: LearnCardQuizSource };

/** A comparable key: two resolutions naming the same key lead to the same card. */
export function learnCardDestinationKey(destination: LearnCardDestination): string {
  switch (destination.kind) {
    case "review": {
      return `review:${destination.card.grapheme}:${drillTypeCodec.encode(destination.card.drillType)}`;
    }
    case "lesson": {
      const part = destination.lesson.part === undefined ? "" : `:${destination.lesson.part}`;
      const pinyin = destination.lesson.pinyin === undefined ? "" : `:${destination.lesson.pinyin}`;
      return `lesson:${destination.lesson.graphemeCard.front}@${destination.lesson.level.index}${part}${pinyin}`;
    }
    case "slide": {
      return `slide:${destination.lesson.slug}`;
    }
    case "quiz": {
      return `quiz:${destination.source}:${destination.quizSlug}`;
    }
    case "summary": {
      return `summary:${destination.reason}`;
    }
    case "exit": {
      return "exit";
    }
  }
}
